import React, { useState } from 'react';
import { Button, Col, Dropdown, Menu, Radio, Row, Segmented, Select, Space, Spin } from 'antd';
import { isEmpty } from 'underscore';
import styles from "../../../Style/DatasetInfo.module.scss";
import {BuyButton, DownloadButton} from "../../DatasetInfoButtons";
import PriceFormatter from "../../../../../../utils/euroPriceFormatter";
import {DownloadCheckboxKey} from "../../../../../../utils/common";
import {DownloadType} from "../../../../../../types/metadata";
import { DownloadOutlined, DownOutlined, LoadingOutlined, ShoppingCartOutlined } from '@ant-design/icons';

interface IPriceSectionProps {
  hasDatasets: boolean;
  price: number;
  isTnw: boolean;
  namePanel: string;
  disableDownload: boolean;
  isLocationAvailable: boolean;
  isDTU: boolean;
  timeStepInterval: string;
  setTimeStepInterval: (value: string) => void;
  datasetId: string;
  addedToCart: boolean;
  showDownloadButton: boolean;
  showCancelDownloadButtonParameters: boolean;
  showCancelDownloadButton2DSpectre: boolean;
  show2DSpectra: boolean;
  isAuthenticated: boolean;
  userId?: string;
  userGroupPermissions: any;
  handleAddToCart: () => void;
  handleRemoveFromCart: () => void;
  handleStartDownloadProcess: (type: DownloadType) => void;
  handleCancelDownload: (type: DownloadType) => void;
  setShowSpectraModal: (value: boolean) => void
  cartLoading: boolean;
  priceLoading: boolean;
  priceParameterSelection: string;
  priceSpectraSelection: string;
  setPriceParameterSelection: (item: string) => void
  setPriceSpectraSelection: (item: string) => void
  label?: string;
}

export const PriceSection = ({
   hasDatasets,
   price,
   isTnw,
   namePanel,
   disableDownload,
   isLocationAvailable,
   isDTU,
   timeStepInterval,
   setTimeStepInterval,
   datasetId,
   addedToCart,
   showDownloadButton,
   showCancelDownloadButtonParameters,
   showCancelDownloadButton2DSpectre,
   show2DSpectra,
   isAuthenticated,
   userId,
   userGroupPermissions,
   handleAddToCart,
   handleRemoveFromCart,
   handleStartDownloadProcess,
   handleCancelDownload,
   setShowSpectraModal,
   cartLoading,
   label,
   priceLoading,
   priceParameterSelection,
   setPriceParameterSelection,
   priceSpectraSelection,
   setPriceSpectraSelection
}: IPriceSectionProps) => {

  const renderActionButton = (
    showDownload: boolean,
    showCancelButton: boolean,
    type: DownloadType,
    disabled: boolean = false,
    authenticatedCheck: boolean = false,
    selectedAction: string,
    setSelectedAction: (value: string) => void
  ) => {
    const startProcess = () => {
      if (authenticatedCheck && !isAuthenticated) {
        setShowSpectraModal(true);
        return;
      }
      handleStartDownloadProcess(type);
    };

    if (showDownload) {
      return selectedAction === 'download' ? (
        <DownloadButton
          id={`${label}_${namePanel}`}
          showCancelDownloadButton={showCancelButton}
          startDownloadProcess={startProcess}
          disableDownloadButtons={disableDownload || disabled}
          handleCancelDownload={() => handleCancelDownload(type)}
          showDropdown={showDownload}
          onSwitchToBuy={() => setSelectedAction('buy')}
        />
      ) : (
        <BuyButton
          id={`${label}_${namePanel}`}
          addedToCart={addedToCart}
          addToCart={handleAddToCart}
          removeFromCart={handleRemoveFromCart}
          cartLoading={cartLoading}
          hasDatasets={hasDatasets}
          disabled={!isLocationAvailable || disabled}
          showDropdown={showDownload}
          onSwitchToDownload={() => setSelectedAction('download')}
        />
      );
    }

    return (
      <BuyButton
        id={`${label}_${namePanel}`}
        addedToCart={addedToCart}
        addToCart={handleAddToCart}
        removeFromCart={handleRemoveFromCart}
        cartLoading={cartLoading}
        hasDatasets={hasDatasets}
        disabled={!isLocationAvailable || disabled}
        showDropdown={showDownload}
      />
    );
  };

  const isSpectraButtonDisabled = () => {
    if (!show2DSpectra) return true;

    if (isAuthenticated) {
      return disableDownload ||
        (!userGroupPermissions[userId || '']?.[namePanel]?.spectraAccess &&
          !showDownloadButton);
    }

    return false;
  };

  const renderPrice = (price: number, isFree: boolean) => {
    if (priceLoading) return <Spin indicator={<LoadingOutlined spin />} size="small" />

    return isFree ? 'Free' : PriceFormatter(price);
  };

  return (
    <Col span={12} className={styles.priceRegion}>
      <Row gutter={16} className={styles.rowHedder}>
        <Col span={8}>{isDTU && 'Time Step'}</Col>
        <Col span={5}>Price[EUR]</Col>
      </Row>

      {hasDatasets && isDTU && (
        <Row
          gutter={16}
          justify="space-between"
          align="middle"
          className={styles.rowBody}
        >
          <Col span={9} className={styles.colText}>
            <Select
              style={{ width: 80 }}
              onChange={val => setTimeStepInterval(val)}
              defaultValue="60"
              value={timeStepInterval}
              disabled={isEmpty(datasetId) || addedToCart}
              size="small"
            >
              <Select.Option value="10">10 min</Select.Option>
              <Select.Option value="15">15 min</Select.Option>
              <Select.Option value="30">30 min</Select.Option>
              <Select.Option value="60">1 hour</Select.Option>
            </Select>
          </Col>
          <Col span={5} className={styles.colPrice}>
            {renderPrice(price, price === 0 && isTnw)}
          </Col>
          <Col span={10} className={styles.buttonRow}>
            {renderActionButton(
              showDownloadButton,
              showCancelDownloadButtonParameters,
              'parameters',
              false,
              false,
              priceParameterSelection,
              setPriceParameterSelection
            )}
          </Col>
        </Row>
      )}

      {hasDatasets && !isDTU && (
        <Row
          gutter={16}
          justify="space-between"
          align="middle"
          className={styles.rowBody}
        >
          <Col span={9} className={styles.colText}>
            Parameters
          </Col>
          <Col span={5} className={styles.colPrice}>
            {renderPrice(price, price === 0 && isTnw)}
          </Col>
          <Col span={10} className={styles.buttonRow}>
            {renderActionButton(
              showDownloadButton,
              showCancelDownloadButtonParameters,
              'parameters',
              false,
              false,
              priceParameterSelection,
              setPriceParameterSelection
            )}
          </Col>
        </Row>
      )}

      {hasDatasets && namePanel === DownloadCheckboxKey.SW && (
        <Row
          gutter={16}
          justify="space-between"
          align="middle"
          className={styles.rowBody}
        >
          <Col span={9} className={show2DSpectra ? styles.colText : styles.disabledColText}>
            2D Spectra
          </Col>
          {show2DSpectra ? (
            <Col span={5} className={styles.colPrice}>
              {renderPrice(price, price === 0 && isTnw)}
            </Col>
          ) : (
            <Col span={5} className={styles.disabledColPrice}>
              -
            </Col>
          )}
          <Col span={10} className={styles.buttonRow}>
            {renderActionButton(
              true,
              showCancelDownloadButton2DSpectre,
              '2DSpectra',
              isSpectraButtonDisabled(),
              true,
              priceSpectraSelection,
              setPriceSpectraSelection
            )}
          </Col>
        </Row>
      )}
    </Col>
  );
};
